import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Resource, ResourceInput } from '@detect/shared';
import { resourceService } from '../../services/resources';
import type { WithId } from '../../services/types';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { clientService } from '../../services/clients';
import { collectorService } from '../../services/collectors';
import { useTenant } from '../../contexts/TenantContext';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { formatRelativeTime } from '../../utils/formatters';
import { PropertiesEditor } from './PropertiesEditor';
import { validateRows, type PropertyRow, type RowErrors } from './customProperties';
import { collectorOptionLabel } from '../collectors/channel';

/**
 * Resource inventory (PRD §2.2). One equality filter at a time (the
 * API's single-filter rule): kind OR collector.
 */

const PAGE = 50;

export function ResourcesPage() {
  const { hasRole } = useTenant();
  const [kindFilter, setKindFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const fetchPage = useCallback(
    (tenantId: string, startAfter: string | undefined) =>
      resourceService.list(tenantId, {
        limit: PAGE,
        startAfter,
        ...(kindFilter ? { kind: kindFilter } : {}),
      }),
    [kindFilter]
  );
  const list = useCursorList<WithId<Resource>>(fetchPage, kindFilter);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Resources</h1>
        <div className="page-actions">
          <label htmlFor="resource-kind-filter" className="visually-hidden">
            Filter by kind
          </label>
          <select
            id="resource-kind-filter"
            name="kindFilter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
          >
            <option value="">All kinds</option>
            <option value="device">Devices</option>
            <option value="website">Websites</option>
            <option value="cloud">Cloud</option>
          </select>
          {hasRole('agent') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
            >
              Add resource
            </button>
          )}
        </div>
      </div>

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No resources yet"
          description="Add a device or website manually, or set up a discovery scan under Discovery."
        />
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Address</th>
                  <th>Monitoring</th>
                  <th>Last data</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/resources/${r.id}`}>{r.displayName ?? r.name}</Link>
                    </td>
                    <td>{r.kind}</td>
                    <td>{r.kind === 'website' ? r.website?.url ?? '—' : r.address ?? '—'}</td>
                    <td>{r.monitoringEnabled ? 'on' : 'off'}</td>
                    <td>{r.lastDataAt ? formatRelativeTime(r.lastDataAt) : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LoadMoreButton
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            loadMoreError={list.loadMoreError}
            onLoadMore={() => void list.loadMore()}
          />
        </>
      )}

      {showCreate && (
        <CreateResourceModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

export function CreateResourceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { tenantId, clientScope } = useTenant();
  const [kind, setKind] = useState<'device' | 'website'>('device');
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [collectorId, setCollectorId] = useState('');
  const [address, setAddress] = useState('');
  const [url, setUrl] = useState('');
  const [regions, setRegions] = useState<ReadonlySet<'us' | 'eu'>>(
    new Set(['us'])
  );
  const [propertyRows, setPropertyRows] = useState<PropertyRow[]>([]);
  const [propertyErrors, setPropertyErrors] = useState<Record<number, RowErrors>>({});
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const collectorsQuery = usePickerOptions(['collectors'], (tid, startAfter) =>
    collectorService.list(tid, { limit: 100, startAfter })
  );
  // Client-scoped users must pick an in-scope client (the API enforces;
  // the picker simply avoids doomed submissions).
  const clients = useMemo(
    () =>
      (clientsQuery.data?.items ?? []).filter(
        (c) => clientScope === null || clientScope.includes(c.id)
      ),
    [clientsQuery.data, clientScope]
  );
  // Collector/client AFFINITY (API-enforced): a client-bound collector
  // serves only that client; null = tenant-shared. Offer only valid
  // pairings for the currently selected client.
  const collectors = useMemo(
    () =>
      (collectorsQuery.data?.items ?? []).filter(
        (c) => c.clientId === null || (clientId !== '' && c.clientId === clientId)
      ),
    [collectorsQuery.data, clientId]
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    // custom.* only, validated against the API's own schema before submit.
    const props = validateRows(propertyRows);
    if (!props.ok) {
      setPropertyErrors(props.errors);
      return;
    }
    setPropertyErrors({});
    const properties = Object.keys(props.properties).length > 0 ? props.properties : undefined;
    setSubmitting(true);
    setError(null);
    try {
      // Devices REQUIRE collector + address (resourceKindRules) — the
      // form marks them required so submissions cannot be doomed.
      const input: ResourceInput =
        kind === 'website'
          ? {
              name,
              kind,
              ...(clientId ? { clientId } : {}),
              ...(properties ? { properties } : {}),
              website: { url, regions: [...regions] },
            }
          : {
              name,
              kind,
              ...(clientId ? { clientId } : {}),
              ...(properties ? { properties } : {}),
              collectorId,
              address,
            };
      await resourceService.create(tenantId, input);
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add resource" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="res-kind">Kind</label>
          <select
            id="res-kind"
            name="kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'device' | 'website')}
          >
            <option value="device">Device</option>
            <option value="website">Website</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="res-name">Name</label>
          <input
            id="res-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="res-client">Client</label>
          <select
            id="res-client"
            name="clientId"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          >
            <option value="">— none (tenant-level) —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {clientsQuery.data?.truncated && (
            <p className="text-muted">Client list truncated — refine via API.</p>
          )}
        </div>
        {kind === 'device' ? (
          <>
            <div className="form-field">
              <label htmlFor="res-collector">Collector</label>
              <select
                id="res-collector"
                name="collectorId"
                required
                value={collectorId}
                onChange={(e) => setCollectorId(e.target.value)}
              >
                <option value="">— select —</option>
                {collectors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {collectorOptionLabel(c, true)}
                  </option>
                ))}
              </select>
              {collectorsQuery.data?.truncated && (
                <p className="text-muted">Collector list truncated.</p>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="res-address">Address (IP or hostname)</label>
              <input
                id="res-address"
                name="address"
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="form-field">
              <label htmlFor="res-url">URL</label>
              <input
                id="res-url"
                name="url"
                type="url"
                required
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <fieldset className="form-field">
              <legend>Check from</legend>
              <p className="field-hint">
                The site counts as down only when every selected region
                fails; a single failing region raises a warning.
              </p>
              {(
                [
                  ['us', 'United States'],
                  ['eu', 'Europe'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="checkbox-label checkbox-inline">
                  <input
                    type="checkbox"
                    checked={regions.has(key)}
                    onChange={(e) =>
                      setRegions((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(key);
                        // Never allow zero regions — a website monitor
                        // with nowhere to run is a lie.
                        else if (next.size > 1) next.delete(key);
                        return next;
                      })
                    }
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          </>
        )}
        <PropertiesEditor
          idPrefix="res-prop"
          rows={propertyRows}
          onChange={(rows) => {
            setPropertyRows(rows);
            setPropertyErrors({});
          }}
          errors={propertyErrors}
        />
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </form>
    </Modal>
  );
}
